import api from './api';

// File size limits (matching backend)
const MAX_IMAGE_SIZE = 20 * 1024 * 1024; // 20 MB
const MAX_DOCUMENT_SIZE = 50 * 1024 * 1024; // 50 MB
const MAX_VIDEO_SIZE = 200 * 1024 * 1024; // 200 MB

// Allowed file types
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/bmp', 'image/svg+xml'];
const ALLOWED_DOCUMENT_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/csv'
];
const ALLOWED_VIDEO_TYPES = ['video/mp4', 'video/mpeg', 'video/quicktime', 'video/x-msvideo', 'video/webm'];

/**
 * Validates file size and type before upload
 * @param {File} file - The file to validate
 * @returns {{ valid: boolean, error?: string }} - Validation result
 */
export const validateFile = (file) => {
  const isImage = file.type.startsWith('image/');
  const isVideo = file.type.startsWith('video/');
  const isDocument = ALLOWED_DOCUMENT_TYPES.includes(file.type);

  // Check file type
  if (!isImage && !isVideo && !isDocument) {
    return {
      valid: false,
      error: `File type "${file.type || 'unknown'}" is not supported. Allowed: images, PDFs, Word, Excel documents.`
    };
  }

  // Check specific type allowlists
  if (isImage && !ALLOWED_IMAGE_TYPES.includes(file.type)) {
    return {
      valid: false,
      error: `Image type "${file.type}" is not supported. Allowed: JPEG, PNG, GIF, WebP, BMP, SVG.`
    };
  }

  if (isVideo && !ALLOWED_VIDEO_TYPES.includes(file.type)) {
    return {
      valid: false,
      error: `Video type "${file.type}" is not supported. Allowed: MP4, MPEG, QuickTime, AVI, WebM.`
    };
  }

  // Check file size
  if (isImage && file.size > MAX_IMAGE_SIZE) {
    return {
      valid: false,
      error: `Image "${file.name}" exceeds maximum size of 20 MB.`
    };
  }

  if (isDocument && file.size > MAX_DOCUMENT_SIZE) {
    return {
      valid: false,
      error: `Document "${file.name}" exceeds maximum size of 50 MB.`
    };
  }

  if (isVideo && file.size > MAX_VIDEO_SIZE) {
    return {
      valid: false,
      error: `Video "${file.name}" exceeds maximum size of 200 MB.`
    };
  }

  return { valid: true };
};

/**
 * Validates multiple files and returns validation results
 * @param {File[]} files - Array of files to validate
 * @returns {{ valid: boolean, errors: string[], validFiles: File[] }}
 */
export const validateFiles = (files) => {
  const errors = [];
  const validFiles = [];

  files.forEach(file => {
    const result = validateFile(file);
    if (result.valid) {
      validFiles.push(file);
    } else {
      errors.push(result.error);
    }
  });

  return {
    valid: errors.length === 0,
    errors,
    validFiles
  };
};

/**
 * Formats file size for display
 * @param {number} bytes - File size in bytes
 * @returns {string} - Formatted file size
 */
export const formatFileSize = (bytes) => {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

/**
 * Uploads a file and returns the presigned URL
 * @param {File} file - The file to upload
 * @returns {Promise<string|null>} - The public URL of the uploaded file, or null on error
 */
export const getPresignedUrl = async (file) => {
  const formData = new FormData();
  formData.append("files", file);

  try {
    const response = await api.post("files/img-upload", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    const presignedUrl = response.data?.response[0]?.publicUrl;
    return presignedUrl;
  } catch (err) {
    console.error("Error getting presigned URL:", err);
    return null;
  }
};

/**
 * Uploads multiple files and returns an array of presigned URLs
 * @param {File[]} files - Array of files to upload
 * @returns {Promise<string[]>} - Array of public URLs
 */
export const getPresignedUrls = async (files) => {
  const uploadPromises = files.map(file => getPresignedUrl(file));
  const urls = await Promise.all(uploadPromises);
  return urls.filter(url => url !== null);
};

/**
 * Checks if a file is an image based on its MIME type
 * @param {File} file - The file to check
 * @returns {boolean} - True if the file is an image
 */
const isImageFile = (file) => {
  return file.type.startsWith('image/');
};

/**
 * Uploads a document (non-image file) and returns the presigned URL
 * @param {File} file - The document file to upload
 * @returns {Promise<string|null>} - The public URL of the uploaded document, or null on error
 */
export const uploadDocument = async (file) => {
  const formData = new FormData();
  formData.append("files", file);

  try {
    const response = await api.post("files/pdf-upload", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    const presignedUrl = response.data?.response[0]?.publicUrl;
    return presignedUrl;
  } catch (err) {
    console.error("Error uploading document:", err);
    return null;
  }
};

/**
 * Uploads files intelligently based on type:
 * - Images use getPresignedUrl
 * - Documents (PDF, etc.) use files/img-upload endpoint directly
 * @param {File[]} files - Array of files to upload
 * @returns {Promise<string[]>} - Array of public URLs
 */
export const uploadServiceContractFiles = async (files) => {
  const uploadPromises = files.map(file => {
    if (isImageFile(file)) {
      return getPresignedUrl(file);
    } else {
      return uploadDocument(file);
    }
  });

  const urls = await Promise.all(uploadPromises);
  return urls.filter(url => url !== null);
};
